import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';
import { Modal, Image, Carousel, Alert, Spin } from 'src/design-system/components/atoms';
import { DownloadOutlined, LoadingOutlined } from '@ant-design/icons';
import PdfRenderer from '../PdfRenderer/PdfRenderer';
import { sanitizeUrl } from '@utils/urlSanitizer';
import { useTheme } from 'src/theming/useTheme';
import { getPdfProxyStreamApi } from '@api/getSignedUrl';
import {
    fetchFileType,
    imageExtensions,
    videoExtensions,
    pdfExtensions,
} from '@components/common/utils';

interface ClassifiedMedia {
    images: any[];
    videos: any[];
    pdfs: any[];
}

interface ProxiedPdfData {
    blobUrl: string;
    fileName: string;
}

const useStyles = createUseStyles((theme: ThemeType) => ({
    modalContainer: {
        padding: `${theme.spacing[4]} ${theme.spacing[0]}`,
        paddingBottom: theme.spacing[8],
    },
    imageGalleryContainer: {
        justifyContent: 'center',
        display: 'flex',
        alignItems: 'center',
        fontSize: theme.typography.fontSize.xxxl,
        gap: theme.spacing[3],
        '& span > svg': {
            width: '120%',
            height: '120%',
        },
    },
    carouselContainer: {
        padding: theme.spacing[6],
        maxWidth: '100%',
    },
    pdfContainer: {
        display: 'flex',
        flexDirection: 'column',
    },
    pdfPanelWrapper: {
        position: 'relative',
    },
    pdfPanelMask: {
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: theme.color.semantic.background.neutral.secondary,
        zIndex: 2,
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '200px',
    },
    sourceText: {
        padding: theme.spacing[2],
        color: theme.color.semantic.text.secondary,
        fontSize: theme.typography.fontSize.sm,
    },
    downloadLink: {
        color: theme.color.semantic.brand.default.primary,
        fontSize: theme.typography.fontSize.lg,
        cursor: 'pointer',
        '&:hover': {
            color: theme.color.semantic.brand.default.secondary,
        },
    },
    mediaContainer: {
        maxWidth: '100%',
    },
}));

const getExtensionFromUrl = (url: string): string | null => {
    try {
        const pathname = new URL(url).pathname;
        const extension = pathname.split('.').pop()?.toLowerCase();
        return extension || null;
    } catch {
        return null;
    }
};

const ensurePdfExtension = (fileName: string): string => {
    const trimmedFileName = fileName.trim();
    if (!trimmedFileName) {
        return 'document.pdf';
    }
    return trimmedFileName.toLowerCase().endsWith('.pdf')
        ? trimmedFileName
        : `${trimmedFileName}.pdf`;
};

const decodeFileName = (fileName: string): string => {
    try {
        return decodeURIComponent(fileName);
    } catch {
        return fileName;
    }
};

const getPdfFileNameFromUrlPath = (url: string): string => {
    try {
        const pathname = new URL(url).pathname;
        const pathFileName = decodeFileName(pathname.split('/').pop() || '');
        return ensurePdfExtension(pathFileName || 'document');
    } catch {
        const pathFileName = decodeFileName((url.split('/').pop() || '').split('?')[0] || '');
        return ensurePdfExtension(pathFileName || 'document');
    }
};

export interface GalleryModalProps {
    handleModalClose: () => void;
    isVisible: boolean;
    imageVideoGalleryData?: ClassifiedMedia;
    mediaList?: any[];
    noModalWrapper?: boolean;
}

const GalleryModal: React.FC<GalleryModalProps> = (props) => {
    const {
        handleModalClose,
        isVisible,
        imageVideoGalleryData,
        mediaList = [],
        noModalWrapper = false,
    } = props;

    const classes = useStyles();
    const theme = useTheme();
    const imageVideoRef = useRef<any>();
    const [failedImages, setFailedImages] = useState<Set<number>>(new Set());
    const [failedVideos, setFailedVideos] = useState<Set<number>>(new Set());
    const [failedPdfs, setFailedPdfs] = useState<Set<number>>(new Set());
    const [corsCheckingPdfs, setCorsCheckingPdfs] = useState<Set<number>>(new Set());
    const [proxiedPdfUrls, setProxiedPdfUrls] = useState<Map<number, string>>(new Map());
    const [proxiedPdfFileNames, setProxiedPdfFileNames] = useState<Map<number, string>>(new Map());
    const [loadedPdfUrls, setLoadedPdfUrls] = useState<Map<number, string>>(new Map());
    const [galleryData, setGalleryData] = useState<ClassifiedMedia>({
        images: imageVideoGalleryData?.images || [],
        videos: imageVideoGalleryData?.videos || [],
        pdfs: imageVideoGalleryData?.pdfs || [],
    });
    const [isClassifying, setIsClassifying] = useState(false);

    useEffect(() => {
        const classifyMediaList = async () => {
            if (!isVisible) {
                return;
            }

            setIsClassifying(true);

            const classified: ClassifiedMedia = {
                images: [],
                videos: [],
                pdfs: [],
            };

            if (mediaList?.length) {
              const classificationPromises = mediaList.map(
                async (item: any) => {
                  const url = item?.url ?? item;
                  if (!url) {
                    return null;
                  }

                  const existingType = item?.type?.toLowerCase();
                  if (existingType) {
                    if ([...pdfExtensions, 'pdf'].includes(existingType)) {
                      return { item, fileType: 'pdf' };
                    }
                    if ([...videoExtensions, 'video'].includes(existingType)) {
                      return { item, fileType: 'video' };
                    }
                    if ([...imageExtensions, 'image'].includes(existingType)) {
                      return { item, fileType: 'image' };
                    }
                  }

                  const extension = getExtensionFromUrl(url);
                  if (extension) {
                    if (pdfExtensions.includes(extension)) {
                      return { item, fileType: 'pdf' };
                    }
                    if (videoExtensions.includes(extension)) {
                      return { item, fileType: 'video' };
                    }
                    if (imageExtensions.includes(extension)) {
                      return { item, fileType: 'image' };
                    }
                  }

                  const { isValid, sanitizedUrl } = sanitizeUrl(url);
                  if (!isValid) {
                    return { item, fileType: 'image' };
                  }
                  const fileType = await fetchFileType(sanitizedUrl);

                  return { item, fileType };
                },
              );

              const results = await Promise.all(classificationPromises);

              results?.forEach((result) => {
                if (!result) return;
                const { item, fileType } = result;

                switch (fileType) {
                  case 'pdf':
                    classified.pdfs.push(item);
                    break;
                  case 'video':
                    classified.videos.push(item);
                    break;
                  case 'image':
                  default:
                    classified.images.push(item);
                    break;
                }
              });
            }

            setGalleryData({
                images: [...(imageVideoGalleryData?.images || []), ...classified.images],
                videos: [...(imageVideoGalleryData?.videos || []), ...classified.videos],
                pdfs: [...(imageVideoGalleryData?.pdfs || []), ...classified.pdfs],
            });
            setIsClassifying(false);
        };

        // TODO: Revisit cleanup lifecycle; 
        // if needed, handle proxied PDF cleanup, error list cleanup in the visible flow.
        classifyMediaList();
    }, [isVisible]);

    const totalImageVideo = () => {
        let total = 0;
        if (galleryData) {
            total += (galleryData.images && Array.isArray(galleryData.images)) ?
            galleryData.images.length: 0;
            total += (galleryData.videos && Array.isArray(galleryData.videos)) ?
            galleryData.videos.length: 0;
            total += (galleryData.pdfs && Array.isArray(galleryData.pdfs)) ?
            galleryData.pdfs.length: 0;
        }
        return total>1;
    };

    const isCorsBlocked = async (url: string): Promise<boolean> => {
        try {
            await fetch(url, { method: 'HEAD', mode: 'cors' });
            return false;
        } catch {
            try {
                await fetch(url, { method: 'HEAD', mode: 'no-cors' });
                return true;
            } catch {
                return false;
            }
        }
    };

    const fetchProxiedPdfUrl = async (url: string): Promise<ProxiedPdfData | null> => {
        const response = await getPdfProxyStreamApi(url);
        if (!response.isSuccess || !response.data) {
            return null;
        }
        const responseFileName = typeof response.filename === 'string' ? response.filename.trim() : '';
        const fileName = ensurePdfExtension(responseFileName || getPdfFileNameFromUrlPath(url));
        return {
            blobUrl: URL.createObjectURL(response.data),
            fileName,
        };
    };

    const galleryContent = (
        <>
            {isClassifying ? (
                <div className={classes.loadingContainer}>
                    <Spin indicator={<LoadingOutlined style={{ fontSize: theme.typography.fontSize.xxxl }} spin />} />
                </div>
            ) : (
            <div>
                <div>
                    <Carousel
                        className={classes.carouselContainer}
                        ref={imageVideoRef}
                        dots={false}
                    >
                        {galleryData?.images?.filter((i: any) => i).map((data: any, index: number) => {
                            const imageUrl = data.url ?? data;
                            const { isValid, sanitizedUrl } = sanitizeUrl(imageUrl);
                            if (!isValid) {
                                return (
                                    <div key={index} className={classes.mediaContainer}>
                                        <Alert
                                            message="Invalid URL blocked"
                                            description="This file may be invalid or inaccessible"
                                            type="error"
                                            showIcon
                                        />
                                    </div>
                                );
                            }
                            const hasError = failedImages.has(index);
                            const handleImageError = () => {
                                setFailedImages(prev => new Set(prev).add(index));
                            };
                            return (
                                <div key={index} className={classes.mediaContainer}>
                                    <div>
                                        {hasError ? (
                                            <Alert
                                                message="Failed to load image"
                                                description="This file may be invalid or inaccessible"
                                                type="error"
                                                showIcon
                                            />
                                        ) : (
                                            <>
                                                <Image.PreviewGroup>
                                                    <Image
                                                        src={sanitizedUrl}
                                                        onError={handleImageError}
                                                    />
                                                </Image.PreviewGroup>
                                                <a
                                                    href={sanitizedUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    download
                                                    className={classes.downloadLink}
                                                >
                                                    <DownloadOutlined />
                                                </a>
                                            </>
                                        )}
                                        {
                                            data.source ? (
                                                <div className={classes.sourceText}>Source: {data.source}</div>
                                            ) : (
                                                <></>
                                            )
                                        }
                                    </div>
                                </div>
                            );
                        })}
                        {galleryData?.videos?.map((data: any, index: number) => {
                            const videoUrl = data.url ?? data;
                            const { isValid, sanitizedUrl } = sanitizeUrl(videoUrl);
                            if (!isValid) {
                                return (
                                    <div key={index} className={classes.mediaContainer}>
                                        <Alert
                                            message="Invalid URL blocked"
                                            description="This file may be invalid or inaccessible"
                                            type="error"
                                            showIcon
                                        />
                                    </div>
                                );
                            }
                            const hasError = failedVideos.has(index);
                            const handleVideoError = () => {
                                setFailedVideos(prev => new Set(prev).add(index));
                            };
                            return (
                                <div key={index} className={classes.mediaContainer}>
                                    {hasError ? (
                                        <Alert
                                            message="Failed to load video"
                                            description="This file may be invalid or inaccessible"
                                            type="error"
                                            showIcon
                                        />
                                    ) : (
                                        <>
                                            <video
                                                controls
                                                style={{ maxWidth: '100%' }}
                                                onError={handleVideoError}
                                            >
                                                <source src={sanitizedUrl} type="video/mp4" />
                                                Your browser does not support the video tag.
                                            </video>
                                            <a
                                                href={sanitizedUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                download
                                                className={classes.downloadLink}
                                            >
                                                <DownloadOutlined />
                                            </a>
                                        </>
                                    )}
                                    {
                                        data.source ? (
                                            <div className={classes.sourceText}>Source: {data.source}</div>
                                        ) : (
                                            <></>
                                        )
                                    }
                                </div>
                            );
                        })}
                        {galleryData?.pdfs?.map((data: any, index: number) => {
                            const pdfUrl = data.url ?? data;
                            const { isValid, sanitizedUrl } = sanitizeUrl(pdfUrl);
                            if (!isValid) {
                                return (
                                    <div key={index} className={classes.pdfContainer}>
                                        <Alert
                                            message="Invalid URL blocked"
                                            description="This file may be invalid or inaccessible"
                                            type="error"
                                            showIcon
                                        />
                                    </div>
                                );
                            }
                            const hasError = failedPdfs.has(index);
                            const isChecking = corsCheckingPdfs.has(index);
                            const proxiedPdfUrl = proxiedPdfUrls.get(index);
                            const proxiedPdfFileName = proxiedPdfFileNames.get(index);
                            const activePdfUrl = proxiedPdfUrl || sanitizedUrl;
                            const isTerminalError = hasError && !isChecking;
                            const isLoadedForCurrentUrl = loadedPdfUrls.get(index) === activePdfUrl;
                            const showPdfMask = !isTerminalError && (!isLoadedForCurrentUrl || isChecking);

                            const handlePdfLoadSuccess = () => {
                                setLoadedPdfUrls(prev => {
                                    const next = new Map(prev);
                                    next.set(index, activePdfUrl);
                                    return next;
                                });
                            };

                            const handlePdfError = async () => {
                                if (isChecking) {
                                    return;
                                }
                                setLoadedPdfUrls(prev => {
                                    const next = new Map(prev);
                                    next.delete(index);
                                    return next;
                                });
                                setFailedPdfs(prev => new Set(prev).add(index));
                                setCorsCheckingPdfs(prev => new Set(prev).add(index));
                                try {
                                    // If proxied URL fails to render, show error and do not retry proxying.
                                    if (proxiedPdfUrl) {
                                        return;
                                    }

                                    const corsBlocked = await isCorsBlocked(sanitizedUrl);
                                    if (corsBlocked) {
                                        const proxyBlobData = await fetchProxiedPdfUrl(sanitizedUrl);
                                        if (proxyBlobData) {
                                            setProxiedPdfUrls(prev => {
                                                const next = new Map(prev);
                                                const existingBlobUrl = next.get(index);
                                                if (typeof existingBlobUrl === 'string') {
                                                    URL.revokeObjectURL(existingBlobUrl);
                                                }
                                                next.set(index, proxyBlobData.blobUrl);
                                                return next;
                                            });
                                            setProxiedPdfFileNames(prev => {
                                                const next = new Map(prev);
                                                next.set(index, proxyBlobData.fileName);
                                                return next;
                                            });
                                            setFailedPdfs(prev => {
                                                const next = new Set(prev);
                                                next.delete(index);
                                                return next;
                                            });
                                        }
                                    }
                                } finally {
                                    setCorsCheckingPdfs(prev => {
                                        const next = new Set(prev);
                                        next.delete(index);
                                        return next;
                                    });
                                }
                            };
                            return (
                                <div key={index} className={classes.pdfContainer}>
                                    {isTerminalError ? (
                                        <Alert
                                            message="Failed to load PDF"
                                            description="This file may be invalid or inaccessible"
                                            type="error"
                                            showIcon
                                        />
                                    ) : (
                                        <div className={classes.pdfPanelWrapper}>
                                            <PdfRenderer
                                                url={activePdfUrl}
                                                downloadFileName={proxiedPdfUrl ? proxiedPdfFileName : undefined}
                                                onError={handlePdfError}
                                                onLoadSuccess={handlePdfLoadSuccess}
                                            />
                                            {showPdfMask ? (
                                                <div className={classes.pdfPanelMask}>
                                                    <Spin indicator={<LoadingOutlined style={{ fontSize: theme.typography.fontSize.xxxl }} spin />} />
                                                </div>
                                            ) : (
                                                <></>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </Carousel>
                </div>
            </div>
            )}
        </>
    );

    if (noModalWrapper) {
        return galleryContent;
    }

    return (
        <Modal
            className={classes.modalContainer}
            visible={isVisible}
            onCancel={handleModalClose}
            footer={null}
            destroyOnClose
        >
            {galleryContent}
        </Modal>
    );
};

export default GalleryModal;
